import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ExternalLink, FileText, Image, Video } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { fromSlug } from '../../lib/slug';
import { AIContentQAPanel } from '../../components/AIContentQAPanel';

type SubmissionStatus = 'SUBMITTED' | 'APPROVED' | 'REVISION_REQUESTED';

interface Submission {
  id: string;
  version: number;
  captionText: string;
  status: SubmissionStatus;
  revisionComments?: string;
  createdAt: string;
  invitation: {
    id: string;
    job: {
      id: string;
      title: string;
      brandOrg: { name: string };
      products: { id: string; name?: string }[];
    };
    kolProfile: {
      id: string;
      name: string;
      user: { fullName: string; email: string };
    };
  };
  media: { id: string; type: string; url: string; title?: string; metadata?: Record<string, unknown> }[];
  createdBy: { id: string; email: string; fullName: string };
}

export default function CorporateSubmissionDetail() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisionComments, setRevisionComments] = useState('');
  const [showRevisionForm, setShowRevisionForm] = useState(false);

  useEffect(() => {
    loadSubmission();
  }, [id]);

  const loadSubmission = async () => {
    try {
      setError(null);
      const response = await api.get(`/submissions/${id}`);
      setSubmission(response.data);
    } catch (err) {
      console.error('Failed to load submission:', err);
      setError('Failed to load submission');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: SubmissionStatus) => {
    const variants: Record<SubmissionStatus, 'default' | 'success' | 'destructive'> = {
      SUBMITTED: 'default',
      APPROVED: 'success',
      REVISION_REQUESTED: 'destructive',
    };
    const labels: Record<SubmissionStatus, string> = {
      SUBMITTED: 'Submitted',
      APPROVED: 'Approved',
      REVISION_REQUESTED: 'Revision Requested',
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      setError(null);
      await api.post(`/submissions/${id}/approve`);
      await loadSubmission();
    } catch (err) {
      console.error('Failed to approve:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!id || !revisionComments.trim()) return;
    try {
      setActionLoading(true);
      setError(null);
      await api.post(`/submissions/${id}/request-revision`, { revisionComments: revisionComments.trim() });
      setShowRevisionForm(false);
      setRevisionComments('');
      await loadSubmission();
    } catch (err) {
      console.error('Failed to request revision:', err);
      setError(err instanceof Error ? err.message : 'Failed to request revision');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!submission) {
    return (
      <div className="text-center py-12 text-gray-500">
        {error || 'Submission not found'}
      </div>
    );
  }

  const inv = submission.invitation;
  const job = inv?.job;
  const kol = inv?.kolProfile;
  const isSubmitted = submission.status === 'SUBMITTED';

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-1 text-sm text-gray-500">
        <button onClick={() => navigate('/brand/submissions')} className="hover:text-purple-600 transition-colors">
          Submissions
        </button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">Submission Review</span>
      </nav>

      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/brand/submissions')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="page-title">Submission Review</h1>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Submission Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Campaign</dt>
              <dd className="mt-1 font-medium">{job?.title || '—'}</dd>
              {job?.brandOrg?.name && (
                <dd className="text-sm text-gray-600">{job.brandOrg.name}</dd>
              )}
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">KOL</dt>
              <dd className="mt-1 font-medium">{kol?.user?.fullName || kol?.name || '—'}</dd>
              {kol?.user?.email && (
                <dd className="text-sm text-gray-600">{kol.user.email}</dd>
              )}
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Version</dt>
              <dd className="mt-1 font-medium">v{submission.version}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">{getStatusBadge(submission.status)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Submitted</dt>
              <dd className="mt-1 text-gray-700">{formatDate(submission.createdAt)}</dd>
            </div>
            {submission.createdBy && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Submitted by</dt>
                <dd className="mt-1 text-gray-700">{submission.createdBy.fullName}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Caption */}
      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Caption
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-gray-700">
            {submission.captionText || <span className="text-gray-400 italic">No caption provided</span>}
          </p>
        </CardContent>
      </Card>

      <AIContentQAPanel
        mode="submission"
        entityId={submission.id}
        description="Pre-screen submission against the campaign brief before final brand approval."
        onUseRevisionFeedback={(feedback) => setRevisionComments(feedback)}
      />

      {/* Media Gallery */}
      {submission.media && submission.media.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                Media ({submission.media.length})
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {submission.media.map((m) => (
                <div key={m.id} className="rounded-lg border bg-gray-50 p-4">
                  {m.type === 'VIDEO' ? (
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-purple-600 hover:text-purple-800 hover:underline"
                    >
                      <Video className="w-5 h-5 shrink-0" />
                      <span className="truncate">{m.title || 'View Video'}</span>
                      <ExternalLink className="w-4 h-4 shrink-0" />
                    </a>
                  ) : (
                    <div className="space-y-2">
                      <img
                        src={m.url}
                        alt={m.title || 'Media'}
                        className="w-full rounded-md object-cover max-h-48"
                      />
                      {m.title && (
                        <p className="text-sm font-medium text-gray-700 truncate">{m.title}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback for already reviewed */}
      {!isSubmitted && (submission.revisionComments || submission.status === 'APPROVED') && (
        <Card>
          <CardHeader>
            <CardTitle>Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            {submission.status === 'APPROVED' ? (
              <p className="text-green-700 font-medium">Content approved</p>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Revision Comments</p>
                <p className="whitespace-pre-wrap text-gray-700">{submission.revisionComments}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions: Approve / Request Revision (only when SUBMITTED) */}
      {isSubmitted && (
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            {showRevisionForm ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="revisionComments" className="block text-sm font-medium text-gray-700 mb-2">
                    Revision comments <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="revisionComments"
                    rows={4}
                    value={revisionComments}
                    onChange={(e) => setRevisionComments(e.target.value)}
                    placeholder="Provide feedback for the KOL..."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    disabled={actionLoading}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRequestRevision}
                    disabled={actionLoading || !revisionComments.trim()}
                  >
                    {actionLoading ? 'Submitting...' : 'Submit Revision Request'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRevisionForm(false);
                      setRevisionComments('');
                    }}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? 'Processing...' : 'Approve'}
                </Button>
                <Button variant="outline" onClick={() => setShowRevisionForm(true)} disabled={actionLoading}>
                  Request Revision
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
